/*
 * Which artwork the dashboard hero draws, and remembering the choice.
 *
 * Kept out of src/theme/ ON PURPOSE. The artwork is not themed -- see the
 * comment at the top of components/DashboardHeroArtwork.jsx, and the test that
 * enforces it, which fails on any import matching /from '.*theme/. Putting
 * this beside themeStore.js would have read as "the artwork follows the
 * theme", which is the one thing it must not do.
 *
 * The choice is LOCAL only. A theme is stored on the server as well, because
 * it is about readability and should follow a person to another device; which
 * mushrooms decorate a card is a per-browser whim, and syncing it would mean a
 * new column, a route change and a migration for a decoration.
 */

//: 'none' is a real choice, not an absence of one. Someone who finds the
//: illustration distracting can turn it off and still keep the watermark.
//:
//: 'mycelium' is the living colony (components/organic/HeroMycelium.jsx): it
//: grows through a soil band, fruits, and settles into a still in about 22
//: seconds (one cycle; a return within 30 minutes shows the still and a
//: single pulse), with a pause control and a still for reduced motion. It is
//: the default, and listed first so the picker offers it first. Only people
//: who never chose get it: a choice is stored only when somebody makes one
//: (storeArtwork, called from the picker), so anyone who picked 'mushrooms'
//: when that was the default still reads 'mushrooms' back.
export const ARTWORKS = ['mycelium', 'mushrooms', 'silhouettes', 'spores', 'none'];
export const DEFAULT_ARTWORK = 'mycelium';

//: The one artwork that moves, and so the one Dashboard renders the animated
//: layer for. DashboardHeroArtwork draws its static part (the soil band).
export const LIVING_ARTWORK = 'mycelium';
export const STORAGE_KEY = 'mb_hero_artwork';

export function isArtwork(value) {
  return ARTWORKS.includes(value);
}

// Storage can be unavailable or blocked (private windows, cleared site data),
// and it can hold a name from a future version that this build does not know.
// Both land on the default rather than on a blank hero.
export function readStoredArtwork() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isArtwork(value) ? value : DEFAULT_ARTWORK;
  } catch {
    return DEFAULT_ARTWORK;
  }
}

export function storeArtwork(artwork) {
  if (!isArtwork(artwork)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, artwork);
  } catch {
    // not persisted; the choice still applies for this session
  }
}

/* ── What the hero shows (2026-10-18) ─────────────────────────────────────
 * Three display modes. 'animation' is the hero as it always was -- the
 * artwork above, the living mycelium by default -- and stays the default:
 * only somebody who picks another mode gets one. 'slideshow' crossfades the
 * bank photographs curators chose for Home (routes/home_slides.py), 'single'
 * shows one of them, still. Per browser, like the artwork. */
export const HERO_MODES = ['animation', 'slideshow', 'single'];
export const DEFAULT_MODE = 'animation';
export const MODE_KEY = 'mb_hero_mode';

//: Seconds each photograph shows in the gallery.
export const SLIDE_INTERVALS = [5, 8, 12];
export const DEFAULT_INTERVAL = 8;
export const INTERVAL_KEY = 'mb_hero_slide_interval';

//: The photograph 'single' shows, when somebody picked one.
export const SINGLE_KEY = 'mb_hero_single_slide';

//: The pause the living mycelium remembers (organic/HeroMycelium.jsx's
//: MOTION_KEY, pinned equal by a test). One switch for every moving hero:
//: whoever paused the colony did not want the photographs moving either.
export const HERO_MOTION_KEY = 'mb_hero_motion';

function readKey(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key, value) {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // not persisted; the choice still applies for this session
  }
}

export function readStoredMode() {
  const value = readKey(MODE_KEY);
  return HERO_MODES.includes(value) ? value : DEFAULT_MODE;
}

export function storeMode(mode) {
  if (HERO_MODES.includes(mode)) writeKey(MODE_KEY, mode);
}

export function readStoredInterval() {
  const value = Number(readKey(INTERVAL_KEY));
  return SLIDE_INTERVALS.includes(value) ? value : DEFAULT_INTERVAL;
}

export function storeInterval(seconds) {
  if (SLIDE_INTERVALS.includes(seconds)) writeKey(INTERVAL_KEY, String(seconds));
}

export function readStoredSingle() {
  return readKey(SINGLE_KEY) || null;
}

export function storeSingle(slideId) {
  writeKey(SINGLE_KEY, slideId || null);
}

export function readHeroPaused() {
  return readKey(HERO_MOTION_KEY) === 'off';
}

export function storeHeroPaused(paused) {
  writeKey(HERO_MOTION_KEY, paused ? 'off' : null);
}

/** The photograph 'single' shows: the one picked, while it is still on
 * Home; else the curators' featured one; else the first. */
export function pickSingle(slides, slideId) {
  if (!slides?.length) return null;
  return slides.find((s) => s.slide_id === slideId)
    || slides.find((s) => s.featured)
    || slides[0];
}

/** For a still gallery (reduced motion, Save-Data): one photograph a day, so
 * Home still changes, just not while somebody is looking at it. */
export function slideOfTheDay(slides, now = new Date()) {
  if (!slides?.length) return null;
  const day = Math.floor((now.getTime() - now.getTimezoneOffset() * 60000) / 86400000);
  return slides[((day % slides.length) + slides.length) % slides.length];
}
